import { useSearchParams } from "react-router-dom";
import { useI18n } from "@/context/I18nContext";
import { useAuth } from "@/context/AuthContext";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { BarChart3, Headphones, ClipboardList, Target, Activity } from "lucide-react";
import { OPS } from "@/constants/testIds";
import SalesPage from "@/pages/SalesPage";
import CallCenterPage from "@/pages/CallCenterPage";
import BranchDsrPage from "@/pages/BranchDsrPage";
import SalesBudgetPage from "@/pages/SalesBudgetPage";
import PosInsightsPage from "@/pages/PosInsightsPage";

const triggerClass =
    "gap-2 rounded-full px-4 py-1.5 text-sm font-semibold text-[color:var(--ink-muted)] transition-[color,background-color] duration-150 ease-out hover:text-[color:var(--ink)] hover:bg-[color:var(--brand-orange-soft)] data-[state=active]:bg-[color:var(--brand-orange)] data-[state=active]:text-white data-[state=active]:shadow-[var(--shadow-sm)]";

const TABS = ["overview", "dsr", "budget", "pos_insights", "call_center"];

export default function SalesHub() {
    const { t } = useI18n();
    const { canSalesBudget } = useAuth();
    // Budget vs Actual is gated by the sales_budget capability (backend-enforced too).
    const canBudget = canSalesBudget;
    // ?tab=<key> lets any card link straight to the sub-tab that fixes its gap.
    const [params, setParams] = useSearchParams();
    const wanted = params.get("tab");
    const tab = TABS.includes(wanted) && (wanted !== "budget" || canBudget) ? wanted : "overview";

    return (
        <div data-testid={OPS.sales.hub} className="space-y-5">
            <Tabs
                value={tab}
                onValueChange={(v) => setParams(v === "overview" ? {} : { tab: v }, { replace: true })}
                className="space-y-5"
            >
                <TabsList className="inline-flex h-auto w-full flex-wrap justify-start gap-1 rounded-full border border-[color:var(--border-soft)] bg-[color:var(--bg-surface)] p-1 sm:w-auto">
                    <TabsTrigger data-testid={OPS.sales.hubTab("overview")} value="overview" className={triggerClass}>
                        <BarChart3 className="h-4 w-4" />
                        {t("cc_tab_sales")}
                    </TabsTrigger>
                    <TabsTrigger data-testid={OPS.sales.hubTab("dsr")} value="dsr" className={triggerClass}>
                        <ClipboardList className="h-4 w-4" />
                        {t("dsr_tab")}
                    </TabsTrigger>
                    {canBudget ? (
                        <TabsTrigger data-testid={OPS.sales.hubTab("budget")} value="budget" className={triggerClass}>
                            <Target className="h-4 w-4" />
                            {t("budget_tab")}
                        </TabsTrigger>
                    ) : null}
                    <TabsTrigger data-testid={OPS.sales.hubTab("pos_insights")} value="pos_insights" className={triggerClass}>
                        <Activity className="h-4 w-4" />
                        {t("pi_tab")}
                    </TabsTrigger>
                    <TabsTrigger data-testid={OPS.sales.hubTab("call_center")} value="call_center" className={triggerClass}>
                        <Headphones className="h-4 w-4" />
                        {t("cc_tab_call_center")}
                    </TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="mt-0 focus-visible:outline-none">
                    <SalesPage />
                </TabsContent>
                <TabsContent value="dsr" className="mt-0 focus-visible:outline-none">
                    <BranchDsrPage />
                </TabsContent>
                {canBudget ? (
                    <TabsContent value="budget" className="mt-0 focus-visible:outline-none">
                        <SalesBudgetPage />
                    </TabsContent>
                ) : null}
                <TabsContent value="pos_insights" className="mt-0 focus-visible:outline-none">
                    <PosInsightsPage />
                </TabsContent>
                <TabsContent value="call_center" className="mt-0 focus-visible:outline-none">
                    <CallCenterPage />
                </TabsContent>
            </Tabs>
        </div>
    );
}
